import { useState, useCallback } from 'react';
import { ChakraProvider } from '@chakra-ui/react';
import { chakraExtendedTheme } from '@/styles/theme';
import { useSeoFetch } from '@/hooks/useSeoFetch';
import useSeoProject from '@/hooks/useSeoProject';
import { FetchApiIds } from '@/types';
import { supabase } from '@/lib/supabase';
import seoAuditsService from '@/services/seoAudits';
import { SeoAudit } from '@/types/api/seo';
import NewAuditModal from '@/components/NewAuditModal';
import SeoAuditListComponent from './SeoAuditList.component';

type AuditsPaginated = { data: SeoAudit[]; count: number; currentPage: number; lastPage: number; nextPage: number | null; prevPage: number | null };

const SeoAuditListPage = function (): JSX.Element {
  const { project } = useSeoProject();
  const projectId = project?.id ?? '';
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { data: auditsData, isLoading, refetch } = useSeoFetch<AuditsPaginated>(
    [FetchApiIds.SEO_AUDITS, projectId],
    () => seoAuditsService.getAll({ projectId, page: 1, take: 50 }),
    { enabled: !!projectId, retry: false }
  );

  const audits = auditsData?.data ?? [];

  const handleCreateAudit = useCallback(async ({ crawlDepth }: { crawlDepth: number }) => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user || !projectId) return;

    // 1. Create audit record (status: queued)
    const audit = await seoAuditsService.create({
      projectId,
      crawlDepth,
      createdBy: user.id,
    });

    // 2. Trigger the crawl (fire and forget)
    seoAuditsService.startCrawl(audit.id, projectId).catch((err) => {
      console.error('[SEOx] Audit crawl failed:', err);
    });

    // 3. Close modal and refresh list
    setIsModalOpen(false);
    refetch();
  }, [projectId, refetch]);

  return (
    <>
      <SeoAuditListComponent
        audits={audits}
        isLoading={isLoading && !!projectId}
        onNewAudit={() => setIsModalOpen(true)}
      />
      <ChakraProvider theme={chakraExtendedTheme}>
        <NewAuditModal
          open={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onSubmit={handleCreateAudit}
          siteUrl={project?.siteUrl ?? undefined}
        />
      </ChakraProvider>
    </>
  );
};

export default SeoAuditListPage;
